import { z } from 'zod'

export const navItemSchema = z.object({
  href: z.string().min(1),
  label: z.string().min(1),
  description: z.string().min(1).optional(),
  external: z.boolean().default(false),
})

export const navGroupSchema = z.object({
  label: z.string().min(1),
  description: z.string().min(1).optional(),
  items: z.array(navItemSchema).min(1),
})

export const navNodeSchema = z.union([navGroupSchema, navItemSchema])

export const actionSchema = z.object({
  href: z.string().min(1),
  label: z.string().min(1),
  variant: z.enum(['primary', 'secondary']).default('primary'),
  external: z.boolean().default(false),
})

export const socialSchema = z.object({
  label: z.string().min(1),
  href: z.string().min(1),
  external: z.boolean().default(true),
})

export const serviceSchema = z.object({
  label: z.string().min(1),
  href: z.string().min(1),
  description: z.string().min(1).optional(),
})

export const seoSchema = z.object({
  title: z.string().min(1),
  description: z.string().min(1),
  keywords: z.array(z.string().min(1)).default([]),
  canonical: z.string().url().optional(),
  ogImage: z.string().url().optional(),
  noIndex: z.boolean().default(false),
})

export const brandSchema = z.object({
  name: z.string().min(1),
  legalName: z.string().min(1),
  description: z.string().min(1),
  tagline: z.string().min(1),
  url: z.string().url(),
  locale: z.string().min(1),
  supportEmail: z.string().email().optional(),
  logo: z.string().min(1),
  logoDark: z.string().min(1),
  mark: z.string().min(1),
  favicon: z.string().min(1),
  appleIcon: z.string().min(1),
  logoAlt: z.string().min(1),
})

export const pageHighlightSchema = z.object({
  icon: z.string().min(1),
  title: z.string().min(1),
  description: z.string().min(1),
})

export const pageCardSchema = z.object({
  title: z.string().min(1),
  description: z.string().min(1),
})

export const tabSchema = z.object({
  value: z.string().min(1),
  title: z.string().min(1),
  description: z.string().min(1),
})

export const statSchema = z.object({
  value: z.string().min(1),
  label: z.string().min(1),
  description: z.string().min(1).optional(),
})

export const logoSchema = z.object({
  label: z.string().min(1),
  href: z.string().min(1).optional(),
  external: z.boolean().default(false),
})

export const socialProofSchema = z.object({
  label: z.string().min(1),
  logos: z.array(logoSchema).default([]),
})

export const solutionSchema = z.object({
  title: z.string().min(1),
  description: z.string().min(1),
  href: z.string().min(1),
  ctaLabel: z.string().min(1),
})

export const caseStudyMetricSchema = z.object({
  value: z.string().min(1),
  label: z.string().min(1),
})

export const caseStudySchema = z.object({
  label: z.string().min(1),
  title: z.string().min(1),
  description: z.string().min(1),
  result: z.string().min(1),
  quote: z.string().min(1),
  author: z.string().min(1),
  role: z.string().min(1),
  company: z.string().min(1),
  href: z.string().min(1).optional(),
  metrics: z.array(caseStudyMetricSchema).default([]),
})

export const testimonialSchema = z.object({
  quote: z.string().min(1),
  author: z.string().min(1),
  role: z.string().min(1),
  company: z.string().min(1),
})

export const faqSchema = z.object({
  question: z.string().min(1),
  answer: z.string().min(1),
})

export const heroImageSchema = z.object({
  src: z.string().min(1),
  alt: z.string().min(1),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
})

export const pageHeroSchema = z.object({
  eyebrow: z.string().min(1).optional(),
  title: z.string().min(1),
  description: z.string().min(1),
  badge: z.string().min(1).optional(),
  primaryCta: actionSchema.optional(),
  secondaryCta: actionSchema.optional(),
  image: heroImageSchema.optional(),
})

export const contentCardSchema = z.object({
  title: z.string().min(1),
  description: z.string().min(1),
  href: z.string().min(1).optional(),
  external: z.boolean().optional(),
  meta: z.string().min(1).optional(),
  tags: z.array(z.string().min(1)).optional(),
})

export const pageSectionSchema = z.object({
  title: z.string().min(1),
  description: z.string().min(1),
  layout: z.enum(['grid', 'list', 'timeline']).optional(),
  items: z.array(contentCardSchema).optional(),
  cta: actionSchema.optional(),
})

export const pageCalloutSchema = z.object({
  eyebrow: z.string().min(1).optional(),
  title: z.string().min(1),
  description: z.string().min(1),
  primaryCta: actionSchema,
  secondaryCta: actionSchema.optional(),
})

export const accordionItemSchema = z.object({
  title: z.string().min(1),
  content: z.string().min(1),
})

export const homePageSchema = z.object({
  seo: seoSchema,
  hero: z.object({
    eyebrow: z.string().min(1),
    title: z.string().min(1),
    description: z.string().min(1),
    primaryCta: actionSchema,
    secondaryCta: actionSchema.optional(),
    image: heroImageSchema.optional(),
  }),
  socialProof: socialProofSchema.optional(),
  stats: z.array(statSchema).default([]),
  servicesPreview: z.object({
    title: z.string().min(1),
    description: z.string().min(1),
  }).optional(),
  solutionsPreview: z.object({
    title: z.string().min(1),
    description: z.string().min(1),
    items: z.array(solutionSchema).default([]),
  }).optional(),
  caseStudyPreview: caseStudySchema.optional(),
  testimonials: z.array(testimonialSchema).default([]),
  faq: z.array(faqSchema).default([]),
  finalCta: z.object({
    eyebrow: z.string().min(1),
    title: z.string().min(1),
    description: z.string().min(1),
    primaryCta: actionSchema,
    secondaryCta: actionSchema.optional(),
  }),
  highlights: z.array(pageHighlightSchema).default([]),
  preview: z.object({
    title: z.string().min(1),
    description: z.string().min(1),
    tabs: z.array(tabSchema).default([]),
    form: z.object({
      nameLabel: z.string().min(1),
      typeLabel: z.string().min(1),
      notesLabel: z.string().min(1),
      notesDefault: z.string().min(1),
      namePlaceholder: z.string().min(1).optional(),
      notesPlaceholder: z.string().min(1).optional(),
    }),
    alert: z.object({
      title: z.string().min(1),
      description: z.string().min(1),
    }),
    tooltip: z.string().min(1),
    dialog: z.object({
      title: z.string().min(1),
      description: z.string().min(1),
      primaryAction: z.string().min(1),
      secondaryAction: z.string().min(1),
    }),
  }),
  accordion: z.object({
    title: z.string().min(1),
    items: z.array(accordionItemSchema).default([]),
  }),
  checklist: z.object({
    title: z.string().min(1),
    items: z.array(z.string().min(1)).default([]),
  }),
})

export const standardPageSchema = z.object({
  seo: seoSchema,
  hero: pageHeroSchema,
  intro: z.string().min(1).optional(),
  stats: z.array(statSchema).default([]),
  sections: z.array(pageSectionSchema).default([]),
  faq: z.array(faqSchema).default([]),
  cta: pageCalloutSchema.optional(),
})

export const detailPageSchema = z.object({
  seo: seoSchema,
  hero: pageHeroSchema,
  intro: z.string().min(1).optional(),
  stats: z.array(statSchema).default([]),
  meta: z.array(pageCardSchema).default([]),
  sections: z.array(pageSectionSchema).default([]),
  related: z.array(contentCardSchema).default([]),
  faq: z.array(faqSchema).default([]),
  cta: pageCalloutSchema.optional(),
})

export const footerSchema = z.object({
  description: z.string().min(1),
  utilityLabel: z.string().min(1),
  socialsLabel: z.string().min(1),
  servicesLabel: z.string().min(1),
  copyright: z.string().min(1),
})

export const navigationSchema = z.object({
  utility: z.array(navItemSchema).default([]),
  actions: z.array(actionSchema).default([]),
  groups: z.array(navNodeSchema).default([]),
})

export const siteContentSchema = z.object({
  brand: brandSchema,
  seo: seoSchema,
  navigation: navigationSchema,
  socials: z.array(socialSchema).default([]),
  services: z.array(serviceSchema).default([]),
  footer: footerSchema,
  pages: z.object({
    home: homePageSchema,
    about: standardPageSchema,
    servicesPage: standardPageSchema,
    solutionsPage: standardPageSchema,
    industriesPage: standardPageSchema,
    caseStudiesPage: standardPageSchema,
    caseStudies: z.object({
      bySlug: z.record(z.string(), detailPageSchema),
    }),
    blogPage: standardPageSchema,
    blogPosts: z.object({
      bySlug: z.record(z.string(), detailPageSchema),
    }),
    careersPage: standardPageSchema,
    careersApplyPage: standardPageSchema,
    contactPage: standardPageSchema,
    pricingPage: standardPageSchema,
    privacyPage: standardPageSchema,
    termsPage: standardPageSchema,
  }),
})

export const siteContentOverrideSchema = z.object({
  brand: brandSchema.partial().optional(),
  seo: seoSchema.partial().optional(),
  navigation: navigationSchema.partial().optional(),
  socials: z.array(socialSchema).optional(),
  services: z.array(serviceSchema).optional(),
  footer: footerSchema.partial().optional(),
  pages: z.object({
    home: homePageSchema.partial().optional(),
  }).partial().optional(),
})

export type NavItem = z.infer<typeof navItemSchema>
export type NavGroup = z.infer<typeof navGroupSchema>
export type NavNode = z.infer<typeof navNodeSchema>
export type Action = z.infer<typeof actionSchema>
export type Social = z.infer<typeof socialSchema>
export type Service = z.infer<typeof serviceSchema>
export type Seo = z.infer<typeof seoSchema>
export type Brand = z.infer<typeof brandSchema>
export type PageHighlight = z.infer<typeof pageHighlightSchema>
export type Stat = z.infer<typeof statSchema>
export type Logo = z.infer<typeof logoSchema>
export type SocialProof = z.infer<typeof socialProofSchema>
export type Solution = z.infer<typeof solutionSchema>
export type CaseStudy = z.infer<typeof caseStudySchema>
export type Testimonial = z.infer<typeof testimonialSchema>
export type FaqItem = z.infer<typeof faqSchema>
export type HeroImage = z.infer<typeof heroImageSchema>
export type PageHero = z.infer<typeof pageHeroSchema>
export type ContentCard = z.infer<typeof contentCardSchema>
export type PageSection = z.infer<typeof pageSectionSchema>
export type PageCallout = z.infer<typeof pageCalloutSchema>
export type HomePageContent = z.infer<typeof homePageSchema>
export type StandardPageContent = z.infer<typeof standardPageSchema>
export type DetailPageContent = z.infer<typeof detailPageSchema>
export type FooterContent = z.infer<typeof footerSchema>
export type NavigationContent = z.infer<typeof navigationSchema>
export type SiteContent = z.infer<typeof siteContentSchema>
export type SiteContentOverride = z.infer<typeof siteContentOverrideSchema>
